// #############################################################################
// #
// #  Chinese translations
// #
// #############################################################################
// This file contains the Chinese translations for the common components of the application.
// It is used across different parts of the application to ensure consistency in language and terminology.
// It is also used to provide a localized experience for chinese-speaking users.
// It includes translations for common phrases, error messages, and UI elements that are used throughout the
// application.
// This helps in making the application more user-friendly and accessible to chinese-speaking audiences.
// It is important to keep this file updated with any new phrases or changes in the application to ensure that
// the translations remain accurate and relevant.
// #############################################################################

export const langZh = {
  home: {
    welcome: '欢迎使用{service}服务',
    enter: '进入'
  },
  account_type: {
    publisher: '发布者',
    consumer: '聚合器',
    author: '作者'
  },
  app: {
    accountNotSubscribedToCurrentService: '您的账户未订阅当前服务'
  },
  login: {
    continue: '继续',
    login_tab: '登录',
    logout: '登出',
    register_tab: '注册',
    loginTitle: '登录您的账户',
    chooseSignInMethod: '选择登录方式',
    noAccount: '没有账户？',
    registerButton: '创建账户',
    cancel: '取消',
    identifier: '标识符',
    identifierDescription: '您可以使用邮箱登录',
    identifierPlaceholder: '输入您的邮箱',
    usernameOrEmail: '用户名或邮箱',
    useAnotherAccount: '使用其他账户',
    username: '用户名',
    password: '密码',
    email: '邮箱',
    loginButton: '登录',
    signingIn: '登录中...',
    signInWithEmail: '发送一次性链接',
    signInWithEmailAgain: '请求新的一次性登录链接',
    signInWithEmailDescription: '使用您的账户邮箱',
    signingInWithMagicLink: '使用魔法链接登录中...',
    signInWithEmailMessageSent: '一次性链接已发送到您的邮箱，请点击链接登录。',
    signingInWithEmail: '发送一次性链接中...',
    subscribeButton: '订阅{type}',
    registerAsButton: '注册为{type}',
    showPassword: '显示密码',
    hidePassword: '隐藏密码',
    or: '或',
    subscriptionSuccess: '订阅成功',
    alternativeAccountDescription: '您的账户当前未订阅当前服务。您可以订阅或切换到以下服务。',
    alternativeAccountGoTo: '前往{type}',
    sameAccount: '您已使用相同账户登录。',
    sessionConflict: {
      title: '会话冲突',
      message: '另一个账户的会话已打开。',
      currentSession: '当前会话:',
      magicLinkSession: '魔法链接会话:',
      question: '您想保持当前会话还是重新连接到魔法链接会话？',
      stayWithCurrent: '保持当前会话',
      reconnectWithMagicLink: '使用魔法链接重新连接'
    },
    error: {
      invalidCredentials: '无效的凭据',
      loginFailed: '登录失败',
      apiConnection: 'API连接错误',
      subscribeFailed: '订阅失败: {error}',
      signInWithEmailFailed: '邮箱登录失败: {error}（请检查您的邮箱或用户名）。',
      invalidEmailFormat: '请输入有效的邮箱地址'
    },
    alternativeAccount: {
      contentCreators: '您是内容创作者吗？',
      contentCreatorsLink: '在此登录',
      authors: '您是作者吗？',
      authorsLink: '在此登录',
      aggregators: '您是聚合器吗？',
      aggregatorsLink: '在此登录',
      author: '作者',
      consumer: '聚合器',
      publisher: '发布者'
    }
  },
  registrationType: {
    publisher: {
      introduction: {
        features: {
          feature1: '',
          feature5: ''
        }
      }
    },
    consumer: {
      introduction: {
        features: {
          feature1: '',
          feature5: ''
        }
      }
    },
    author: {
      introduction: {
        features: {
          feature1: '',
          feature5: ''
        }
      }
    }
  },
  registration: {
    title: '账户注册',
    introduction: {
      title: '欢迎注册',
      subtitle: '注册{service}服务',
      description: '您即将注册{service}服务。此注册将使您能够访问{service}账户的所有可用功能和能力。',
      startRegistration: '开始注册',
      alreadyHaveAccount: '已有账户？登录'
    },
    step_introduction: {
      title: '介绍'
    },
    step_account_setup: {
      title: '账户设置',
      displayName: '显示名称',
      displayNamePlaceholder: '输入您的显示名称',
      username: '用户名',
      usernamePlaceholder: '输入您的用户名',
      confirmPassword: '确认密码',
      passwordsDoNotMatch: '密码不匹配',
      password: '密码',
      email: '邮箱',
      emailPlaceholder: '输入您的邮箱地址',
      affiliation: '所属机构',
      affiliationPlaceholder: '大学、公司或机构',
      homePage: '主页/主页面',
      homePagePlaceholder: 'https://your-website.com',
      submit: '提交个人资料',
      submitting: '提交中...'
    },
    step_account_verification: {
      title: '账户验证',
      tokenLabel: '验证令牌',
      tokenPlaceholder: '输入令牌',
      checkEmail: '我们已向您的地址发送验证邮件。请检查收件箱并按照说明验证您的账户。',
      tokenSent: '验证令牌已发送至: {email}',
      resendToken: '重新发送令牌',
      verifyAccount: '验证账户',
      sendingEmail: '发送邮件中...',
      resendEmail: '重新发送邮件',
      accountVerified: '账户验证成功！',
      redirecting: '正在重定向到下一步...',
      verifying: '检查激活',
      sendingToken: '发送验证令牌中...',
      invalidToken: '无效的验证令牌。请重试。',
      tooManyAttempts: '失败尝试过多。请请求新令牌。',
      noToken: '未找到验证令牌。请请求新令牌。',
      verificationSuccess: '邮箱验证成功！',
      attemptsRemaining: '剩余{count}次尝试',
      resendSuccess: '新的验证令牌已发送到您的邮箱。',
      networkError: '网络错误。请检查您的连接并重试。',
      checkingActivation: '检查账户激活中...',
      emailSentTo: '邮件发送至:',
      activationEmailResent: '激活邮件发送成功！请检查您的收件箱。',
      resendFailed: '发送激活邮件失败。请重试。',
      missingIdentifierOrRegistrationToken: '缺少标识符或注册令牌',
      accountDoesNotHaveAuthorPrivileges: '账户没有作者权限。请联系支持。',
      accountActivationCheckFailed: '账户激活检查失败。请重试。',
      accountNotYetActivated: '账户尚未激活。请检查您的邮箱并点击激活链接。',
      invalidCredentials: '无效的凭据。请检查您的标识符和密码。',
      failedToVerifyAccountActivation: '验证账户激活失败。请重试。',
      failedToResendActivationEmail: '重新发送激活邮件失败。请重试。',
      accountRegistrationRejected: '账户注册被拒绝。请联系支持。'
    },
    step_registration_complete: {
      title: '完成注册',
      registrationSummary: '注册摘要',
      profileCreated: '个人资料已创建',
      emailsSent: '已向发布者发送邮件',
      continueToDashboard: '继续到仪表板',
      continueToLogin: '继续到登录'
    }
  },
  common: {
    cancel: '取消',
    save: '保存'
  },
  navbar: {
    docs: '文档',
    account: '账户',
    settings: '设置',
    logout: '登出',
    login: '登录',
    pendingRegistrationDesc: '请完成注册以访问此服务。',
    apps: {
      title: '应用',
      publisher: '发布者',
      publisherDesc: '切换到发布者服务',
      author: '作者',
      authorDesc: '切换到作者服务',
      consumer: '聚合器',
      consumerDesc: '切换到聚合器服务',
      website: '网站',
      websiteDesc: '切换到网站',
      portal: '门户',
      portalDesc: '切换到门户',
      currentApplicationDesc: '当前服务',
      unavailableServiceDesc: '不可用服务'
    }
  },
  sidebar: {
    expand: '展开',
    collapse: '折叠',
    info: '信息',
    infoDesc: '关于服务的信息',
    showDescriptions: '显示描述',
    hideDescriptions: '隐藏描述'
  },
  pagination: {
    previous: '上一页',
    next: '下一页',
    first: '首页',
    last: '末页',
    page: '页面 {current} 到 {total}',
    itemsPerPage: '每页项目数'
  },
  table: {
    loading: '加载中...',
    noData: '无数据',
    noResults: '无结果',
    search: '搜索'
  },
  form: {
    submit: '提交',
    cancel: '取消',
    save: '保存',
    loading: '加载中...'
  },
  modal: {
    close: '关闭',
    confirm: '确认',
    cancel: '取消',
    save: '保存'
  },
  alert: {
    success: '成功',
    error: '错误',
    warning: '警告',
    info: '信息'
  },
  toast: {
    success: '成功',
    error: '错误',
    warning: '警告',
    info: '信息',
    close: '关闭'
  },
  tooltip: {
    success: '成功',
    error: '错误',
    warning: '警告',
    info: '信息'
  },
  notification: {
    success: '成功',
    error: '错误',
    warning: '警告',
    info: '信息'
  },
  notifications: {
    title: '通知',
    empty: '无通知',
    loading: '加载通知中...',
    pageInfo: '第{current}页，共{total}页',
    error: '加载通知错误',
    markAsRead: '标记为已读',
    markAsUnread: '标记为未读',
    open: '打开',
    markAllAsRead: '全部标记为已读',
    unread: '未读',
    all: '全部',
    noContent: '无可用内容',
    close: '关闭',
    reload: '重新加载通知',
    showMore: '显示更多',
    loadingMore: '加载更多通知中...',
    errorLoadingMore: '加载更多通知错误',
    noMoreNotifications: '没有更多通知',
    noNotifications: '无通知',
    noNotificationsFound: '未找到通知'
  },
  account: {
    title: '账户信息',
    profile_section: '个人资料',
    api_keys_section: 'API密钥',
    security_section: '安全',
    loading: '加载账户信息中...',
    profile: '个人资料',
    authorProfile: '作者个人资料',
    publisherProfile: '发布者个人资料',
    aggregatorProfile: '聚合器个人资料',
    id: 'ID',
    name: '姓名',
    email: '邮箱',
    services: '服务',
    memberSince: '注册时间',
    lastLogin: '最后登录',
    createNewApiKey: '创建新API密钥',
    apiKeysTitle: 'API密钥',
    apiKeys: {
      name: '名称',
      keyId: '密钥ID',
      status: '状态',
      lastUsed: '最后使用',
      created: '创建时间',
      expires: '过期时间',
      neverUsed: '从未使用',
      noKeys: '未找到API密钥。',
      advancedControls: '对于高级控制，请访问',
      advancedControlsLink: '账户管理页面'
    },
    security: {
      changePassword: '修改密码',
      changePasswordDescription: '更新您的账户密码。您需要输入当前密码以确认更改。',
      currentPassword: '当前密码',
      currentPasswordPlaceholder: '输入当前密码',
      newPassword: '新密码',
      newPasswordPlaceholder: '输入新密码',
      confirmPassword: '确认新密码',
      confirmPasswordPlaceholder: '再次输入新密码',
      changePasswordButton: '修改密码',
      changingPassword: '修改中...',
      passwordChangedSuccess: '您的密码已成功修改。',
      passwordChangeFailed: '密码修改失败。请检查当前密码后重试。',
      resetPassword: '重置密码',
      resetPasswordDescription: '忘记密码？我们可以向您的注册邮箱发送重置链接。点击邮件中的链接即可设置新密码。',
      sendResetEmailButton: '发送密码重置邮件',
      sendingResetEmail: '发送中...',
      resetEmailSent: '已向您的注册地址发送密码重置邮件。请查收并点击链接设置新密码。',
      resetPasswordFailed: '密码重置邮件发送失败。请重试。',
      changePrimaryEmail: '修改主要邮箱',
      changePrimaryEmailDescription: '更新您的主要邮箱地址。系统将向新地址发送验证邮件。新邮箱仅在验证后才会激活。',
      newEmail: '新邮箱',
      newEmailPlaceholder: '输入新的邮箱地址',
      confirmEmail: '确认新邮箱',
      confirmEmailPlaceholder: '再次输入新的邮箱地址',
      emailsDoNotMatch: '邮箱地址不匹配',
      changeEmailButton: '发送验证邮件',
      sendingVerification: '发送中...',
      emailVerificationSent: '已向您的新地址发送验证邮件。请查收并按照说明激活新邮箱。',
      emailChangeFailed: '邮箱修改请求失败。请重试。'
    }
  },
  profileSetup: {
    displayName: '显示名称',
    displayNamePlaceholder: '输入您的显示名称',
    displayNameHelp: '您的显示名称将用于在系统中识别您（这不是您的用户名）。',
    title: '个人资料设置',
    description: '请提供您的专业信息以完成您的个人资料。',
    affiliation: '所属机构',
    affiliationPlaceholder: '例如：剑桥大学、MIT、Google Research',
    areaOfInterest: '兴趣领域',
    areaOfInterestPlaceholder: '例如：机器学习、计算机视觉、NLP',
    homePage: '主页',
    homePagePlaceholder: 'https://your-website.com',
    secondaryEmails: '备用邮箱',
    addSecondaryEmail: '添加备用邮箱',
    secondaryEmailPlaceholder: '您的邮箱地址',
    secondaryEmailsHelp: '您的备用邮箱',
    save: '保存个人资料',
    saving: '保存中...',
    nextPage: '下一页',
    update: '更新个人资料',
    updating: '更新中...',
    avatarUrl: '头像URL',
    avatarUrlPlaceholder: 'https://example.com/avatar.jpg',
    changeAvatar: '更改头像',
    noAvatar: '无头像',
    success: {
      saved: '个人资料保存成功！'
    },
    errors: {
      affiliationRequired: '所属机构为必填项。',
      areaOfInterestRequired: '兴趣领域为必填项。',
      invalidHomePage: '请输入有效的主页URL。',
      tooManySecondaryEmails: '最多可添加5个备用邮箱。',
      invalidSecondaryEmail: '请输入有效的邮箱地址。',
      saveFailed: '保存个人资料失败。请重试。',
      invalidAvatarUrl: '请输入有效的头像URL。'
    }
  },
  profile: {
    key_DISPLAY_NAME: '显示名称',
    key_NAME: '姓名',
    key_EMAIL: '邮箱',
    key_AFFILIATION: '所属机构',
    key_AREA_OF_INTEREST: '兴趣领域',
    key_HOME_PAGE: '主页',
    key_EMAILS: '邮箱',
    key_AVATAR_URL: '头像URL',
    key_ORCID: 'ORCID',
    key_LINKEDIN: 'LinkedIn',
    key_GOOGLE_SCHOLAR: 'Google Scholar'
  },
  profileSetupView: {
    title: '个人资料设置',
    description: '请设置您的个人资料以访问此服务。',
    update: '更新个人资料',
    editProfile: '编辑个人资料'
  },
  bankInfoForm: {
    title: '添加/更新银行信息',
    accountHolder: '账户持有人姓名:',
    iban: 'IBAN:',
    bic: 'BIC/SWIFT代码:',
    save: '保存银行信息',
    saved: '银行信息已本地保存（此演示用）。'
  },
  settings: {
    title: '设置',
    theme: '主题',
    light: '浅色',
    dark: '深色',
    layout: '布局',
    helpEnabled: '显示帮助消息',
    sidebar: '侧边栏',
    navbar: '导航栏',
    save: '保存',
    saved: '设置已保存！',
    language: '语言',
    experimentations: '实验',
    enabled: '已启用',
    disabled: '已禁用'
  },
  apiKeyModal: {
    title: '创建新API密钥',
    namePlaceholder: 'API密钥名称',
    nameHelp: '输入API密钥的名称，这将帮助您稍后识别该密钥。',
    createButton: '创建API密钥',
    yourKeyIs: '您的新API密钥是:',
    copyWarning: '复制并保存此密钥。您将无法再次查看它！',
    copy: '复制',
    copied: '已复制！',
    close: '关闭',
    error: '创建API密钥失败。'
  },
  lang: {
    en: 'EN',
    fr: 'FR',
    de: 'DE',
    es: 'ES'
  },
  theme: {
    switchToLight: '切换到浅色模式',
    switchToDark: '切换到深色模式'
  }
};
